import imageCompression from "browser-image-compression";
import { ALLOWED_UPLOAD_TYPES, MAX_UPLOAD_BYTES } from "@garage/shared";
import { api } from "../api/client";

/**
 * A 12 MP photo of an invoice is ~4 MB and completely unreadable gains over
 * ~1600px. Compressing in the browser keeps R2 free-tier storage irrelevant
 * and makes the upload finish before you have put your phone away.
 */
async function shrink(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  try {
    return await imageCompression(file, {
      maxWidthOrHeight: 1600,
      maxSizeMB: 1,
      useWebWorker: true,
      // HEIC and friends are not universally viewable; normalise to JPEG.
      fileType: "image/jpeg",
    });
  } catch {
    return file; // compression is an optimisation, never a blocker
  }
}

export async function uploadAttachment(
  serviceId: string,
  original: File,
): Promise<void> {
  const file = await shrink(original);

  if (!(ALLOWED_UPLOAD_TYPES as readonly string[]).includes(file.type)) {
    throw new Error(`${original.name} is not a PDF or an image`);
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`${original.name} is larger than 10 MB`);
  }

  const meta = {
    filename: original.name.slice(0, 200),
    mimeType: file.type,
    sizeBytes: file.size,
    kind: "INVOICE" as const,
  };

  const { key, uploadUrl } = await api<{ key: string; uploadUrl: string }>(
    `/api/services/${serviceId}/attachments/sign`,
    { method: "POST", body: meta },
  );

  // Straight to R2 — the file never passes through our API.
  const put = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload failed for ${original.name}`);

  await api(`/api/services/${serviceId}/attachments`, {
    method: "POST",
    body: { ...meta, key },
  });
}
